/**
 * Analytics API Client
 * Fetches user behavior analytics data from PostHog via backend
 */

import apiClient from './client';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';

// PostHog HogQL aggregations over wide ranges (90d / 1y) can take far longer
// than the default 30s apiClient timeout. The backend caps the PostHog query
// at 60s + caches results, so give the first (cache-miss) request room to
// finish instead of axios aborting at 30s with "timeout of 30000ms exceeded".
const ANALYTICS_TIMEOUT_MS = 90000;

function buildAnalyticsParams(params?: {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
}) {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('start_date', params.startDate);
  if (params?.endDate) searchParams.set('end_date', params.endDate);
  searchParams.set('exclude_team', String(params?.excludeTeam ?? DEFAULT_EXCLUDE_TEAM));
  return searchParams;
}

// ============================================================
// Types
// ============================================================

export interface EventCount {
  event: string;
  count: number;
  unique_users: number;
}

export interface DailyEventStats {
  date: string;
  total_events: number;
  unique_users: number;
  events_breakdown: Record<string, number>;
}

export interface UserBehaviorSummary {
  total_events: number;
  unique_users: number;
  avg_events_per_user: number;
  top_events: EventCount[];
  daily_stats: DailyEventStats[];
}

export interface FeatureUsageStats {
  feature_name: string;
  click_count: number;
  unique_users: number;
  avg_duration_ms: number | null;
  success_rate: number | null;
}

export interface ScreenViewStats {
  screen_name: string;
  view_count: number;
  unique_users: number;
  avg_time_spent_ms: number | null;
}

export interface UserJourneyStep {
  step_name: string;
  users: number;
  conversion_rate: number;
}

export interface AnalyticsDashboard {
  timestamp: string;
  date_range: {
    start: string;
    end: string;
  };
  summary: UserBehaviorSummary;
  feature_usage: FeatureUsageStats[];
  screen_views: ScreenViewStats[];
  user_journey: UserJourneyStep[];
  retention: Record<string, number>;
}

export interface AnalyticsHealthStatus {
  status: 'healthy' | 'not_configured' | 'error';
  message?: string;
  host?: string;
}

// ============================================================
// API Functions
// ============================================================

/**
 * Get comprehensive analytics dashboard data
 */
export async function getAnalyticsDashboard(params?: {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
}): Promise<AnalyticsDashboard> {
  const searchParams = buildAnalyticsParams(params);

  const query = searchParams.toString();
  const url = `/analytics/dashboard${query ? `?${query}` : ''}`;

  const response = await apiClient.get(url, { timeout: ANALYTICS_TIMEOUT_MS });
  return response.data;
}

/**
 * Get event counts with optional filtering
 */
export async function getEventCounts(params?: {
  startDate?: string;
  endDate?: string;
  events?: string[];
  excludeTeam?: boolean;
}): Promise<EventCount[]> {
  const searchParams = buildAnalyticsParams(params);
  if (params?.events) searchParams.set('events', params.events.join(','));

  const query = searchParams.toString();
  const url = `/analytics/events${query ? `?${query}` : ''}`;

  const response = await apiClient.get(url, { timeout: ANALYTICS_TIMEOUT_MS });
  return response.data;
}

/**
 * Get feature usage statistics
 */
export async function getFeatureUsage(params?: {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
}): Promise<FeatureUsageStats[]> {
  const searchParams = buildAnalyticsParams(params);

  const query = searchParams.toString();
  const url = `/analytics/features${query ? `?${query}` : ''}`;

  const response = await apiClient.get(url, { timeout: ANALYTICS_TIMEOUT_MS });
  return response.data;
}

/**
 * Get screen view statistics
 */
export async function getScreenViews(params?: {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
}): Promise<ScreenViewStats[]> {
  const searchParams = buildAnalyticsParams(params);

  const query = searchParams.toString();
  const url = `/analytics/screens${query ? `?${query}` : ''}`;

  const response = await apiClient.get(url, { timeout: ANALYTICS_TIMEOUT_MS });
  return response.data;
}

/**
 * Get user journey funnel data
 */
export async function getUserJourneyFunnel(params?: {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
}): Promise<UserJourneyStep[]> {
  const searchParams = buildAnalyticsParams(params);

  const query = searchParams.toString();
  const url = `/analytics/funnel${query ? `?${query}` : ''}`;

  const response = await apiClient.get(url, { timeout: ANALYTICS_TIMEOUT_MS });
  return response.data;
}

/**
 * Get retention rates
 */
export async function getRetentionRates(params?: {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
}): Promise<Record<string, number>> {
  const searchParams = buildAnalyticsParams(params);

  const query = searchParams.toString();
  const url = `/analytics/retention${query ? `?${query}` : ''}`;

  const response = await apiClient.get(url, { timeout: ANALYTICS_TIMEOUT_MS });
  return response.data;
}

/**
 * Check analytics health status
 */
export async function getAnalyticsHealth(): Promise<AnalyticsHealthStatus> {
  const response = await apiClient.get('/analytics/health');
  return response.data;
}
